import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useCart } from "../context/CartContext";
import {
  ArrowLeft, MapPin, Star, Clock, Search, Leaf,
  SlidersHorizontal, Plus, Minus, ShoppingCart, UtensilsCrossed,
  Tag, ChevronRight, X, Share2, Heart, BadgePercent, Zap,
  CheckCircle, Phone, Info,
} from "lucide-react";

const C = {
  primary:  "#f97316", dark:  "#ea580c", light: "#fff7ed", mid:   "#fed7aa",
  white:    "#ffffff", bg:    "#f2f2f7", text:  "#1c1c1e", sub:   "#6b7280",
  muted:    "#9ca3af", border:"#e5e7eb", green: "#16a34a", greenBg:"#f0fdf4",
  greenBorder:"#bbf7d0", red:"#ef4444", redBg:"#fef2f2", yellow:"#f59e0b",
};

const BASE = typeof window !== "undefined" && window.location.hostname !== "localhost"
  ? "https://quickry-ivrv.onrender.com/api" : "http://localhost:5000/api";

// ── Veg Dot ───────────────────────────────────────
function VegDot({ veg, size = 16 }) {
  const col = veg ? C.green : C.red;
  return (
    <div style={{ width:size, height:size, border:`1.5px solid ${col}`, borderRadius:3, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
      <div style={{ width:size*0.5, height:size*0.5, borderRadius:"50%", background:col }}/>
    </div>
  );
}

// ── Qty Stepper ───────────────────────────────────
function QtyBtn({ qty, onAdd, onDec }) {
  if (qty === 0) return (
    <button onClick={onAdd} style={{ background:C.white, border:`1.5px solid ${C.primary}`, borderRadius:8, width:80, height:34, display:"flex", alignItems:"center", justifyContent:"center", gap:4, cursor:"pointer", boxShadow:"0 2px 8px rgba(249,115,22,0.15)" }}>
      <Plus size={14} color={C.primary} strokeWidth={2.5}/>
      <span style={{ color:C.primary, fontWeight:800, fontSize:"0.82rem" }}>ADD</span>
    </button>
  );
  return (
    <div style={{ background:C.primary, borderRadius:8, width:80, height:34, display:"flex", alignItems:"center", justifyContent:"space-between", padding:"0 6px" }}>
      <button onClick={onDec} style={{ background:"none", border:"none", cursor:"pointer", display:"flex", alignItems:"center", padding:4 }}>
        <Minus size={13} color="#fff" strokeWidth={3}/>
      </button>
      <span style={{ color:"#fff", fontWeight:800, fontSize:"0.88rem" }}>{qty}</span>
      <button onClick={onAdd} style={{ background:"none", border:"none", cursor:"pointer", display:"flex", alignItems:"center", padding:4 }}>
        <Plus size={13} color="#fff" strokeWidth={3}/>
      </button>
    </div>
  );
}

export default function RestaurantMenu() {
  const { id }      = useParams();
  const navigate    = useNavigate();
  const { cart, addToCart, increaseQty, decreaseQty } = useCart();

  const [restaurant, setRestaurant] = useState(null);
  const [products,   setProducts]   = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat,  setActiveCat]  = useState("");
  const [vegOnly,    setVegOnly]    = useState(false);
  const [nonVegOnly, setNonVegOnly] = useState(false);
  const [search,     setSearch]     = useState("");
  const [srchOpen,   setSrchOpen]   = useState(false);
  const [loading,    setLoading]    = useState(true);
  const [scrolled,   setScrolled]   = useState(false);
  const [liked,      setLiked]      = useState(false);
  // Gallery images state
  const [galleryIdx, setGalleryIdx] = useState(0);
  const catBarRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [rRes, pRes] = await Promise.all([
          axios.get(`${BASE}/restaurants/${id}`),
          axios.get(`${BASE}/menu/restaurant/${id}`),
        ]);
        const r     = rRes.data.data || rRes.data;
        const prods = pRes.data.data || pRes.data || [];
        setRestaurant(r);
        setProducts(prods);
        const cats = [];
        prods.forEach(p => {
          const cat = p.category || "Menu";
          if (cat && !cats.includes(cat)) cats.push(cat);
        });
        setCategories(cats);
        if (cats.length) setActiveCat(cats[0]);
      } catch (e) {
        console.error("Menu load failed:", e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 220);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  // Auto-scroll gallery
  useEffect(() => {
    const imgs = restaurant?.images || (restaurant?.image ? [restaurant.image] : []);
    if (imgs.length <= 1) return;
    const t = setInterval(() => setGalleryIdx(i => (i + 1) % imgs.length), 3000);
    return () => clearInterval(t);
  }, [restaurant]);

  const getQty    = (pid) => { const item = cart.find(i => i._id === pid); return item ? item.qty : 0; };
  const totalItems = cart.reduce((s, i) => s + i.qty, 0);
  const totalAmt   = cart.reduce((s, i) => s + i.price * i.qty, 0);

  const filtered = products.filter(p => {
    const cat = p.category || "Menu";
    if (activeCat && cat !== activeCat) return false;
    if (vegOnly && !p.isVeg) return false;
    if (nonVegOnly && p.isVeg) return false;
    if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const scrollToCat = (cat) => {
    setActiveCat(cat);
    catBarRef.current?.querySelector(`[data-cat="${cat}"]`)?.scrollIntoView({ inline:"center", behavior:"smooth", block:"nearest" });
  };

  if (loading) return (
    <div style={{ minHeight:"100vh", background:C.bg, display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:14 }}>
      <div style={{ width:48, height:48, borderRadius:16, background:"linear-gradient(135deg,#ea580c,#f97316)", display:"flex", alignItems:"center", justifyContent:"center" }}>
        <UtensilsCrossed size={24} color="#fff" strokeWidth={1.8}/>
      </div>
      <div style={{ fontSize:"0.9rem", color:C.sub, fontWeight:600 }}>Loading menu...</div>
    </div>
  );

  const r = restaurant || {};

  // ── Gallery images — support single or multiple ──
  const galleryImages = r.images?.length
    ? r.images
    : r.image
    ? [r.image]
    : [];

  return (
    <div style={{ fontFamily:"'Plus Jakarta Sans',system-ui,sans-serif", background:C.bg, minHeight:"100vh", color:C.text, paddingBottom: totalItems > 0 ? 100 : 20 }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,800;9..144,900&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
        ::-webkit-scrollbar{display:none}
        button,input{font-family:inherit;outline:none;border:none}
        @keyframes slideUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
        @keyframes fadeIn{from{opacity:0}to{opacity:1}}
        @keyframes spin{to{transform:rotate(360deg)}}
        .item-card:hover{box-shadow:0 4px 20px rgba(0,0,0,0.08)!important}
        .cat-tab:active{opacity:.7}
        .btn-press:active{transform:scale(.95)}
      `}</style>

      {/* ══ HERO — Restaurant Photo (Zomato style) ══ */}
      <div style={{ position:"relative", height:240, overflow:"hidden", background:"linear-gradient(135deg,#c2410c,#ea580c,#f97316)" }}>

        {/* ── Scrolling gallery images ── */}
        {galleryImages.length > 0 ? (
          <>
            {galleryImages.map((img, i) => (
              <img key={i} src={img} alt={r.name}
                style={{
                  position:"absolute", inset:0, width:"100%", height:"100%",
                  objectFit:"cover",
                  opacity: i === galleryIdx ? 1 : 0,
                  transition:"opacity 0.7s ease",
                }}
              />
            ))}
            {/* Dark overlay */}
            <div style={{ position:"absolute", inset:0, background:"rgba(0,0,0,0.25)" }}/>

            {/* Dot indicators */}
            {galleryImages.length > 1 && (
              <div style={{ position:"absolute", bottom:60, left:0, right:0, display:"flex", justifyContent:"center", gap:6, zIndex:5 }}>
                {galleryImages.map((_, i) => (
                  <div key={i} onClick={() => setGalleryIdx(i)}
                    style={{ width: i === galleryIdx ? 20 : 6, height:6, borderRadius:100, background: i === galleryIdx ? "#fff" : "rgba(255,255,255,0.5)", cursor:"pointer", transition:"all 0.3s" }}/>
                ))}
              </div>
            )}
          </>
        ) : (
          /* No image — fallback gradient with icon */
          <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", opacity:.1 }}>
            <UtensilsCrossed size={160} color="#fff" strokeWidth={0.5}/>
          </div>
        )}

        {/* Bottom fade */}
        <div style={{ position:"absolute", bottom:0, left:0, right:0, height:100, background:"linear-gradient(to top, rgba(242,242,247,1), transparent)" }}/>

        {/* Top bar */}
        <div style={{ position:"absolute", top:0, left:0, right:0, display:"flex", alignItems:"center", justifyContent:"space-between", padding:"48px 16px 14px" }}>
          <button onClick={() => navigate(-1)} className="btn-press"
            style={{ width:38, height:38, borderRadius:12, background:"rgba(0,0,0,0.45)", backdropFilter:"blur(10px)", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>
            <ArrowLeft size={18} color="#fff"/>
          </button>
          <div style={{ display:"flex", gap:8 }}>
            <button className="btn-press" onClick={() => setSrchOpen(true)}
              style={{ width:38, height:38, borderRadius:12, background:"rgba(0,0,0,0.45)", backdropFilter:"blur(10px)", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>
              <Search size={17} color="#fff"/>
            </button>
            <button className="btn-press" onClick={() => setLiked(l => !l)}
              style={{ width:38, height:38, borderRadius:12, background:"rgba(0,0,0,0.45)", backdropFilter:"blur(10px)", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>
              <Heart size={17} color="#fff" fill={liked ? "#fff" : "none"}/>
            </button>
          </div>
        </div>
      </div>

      {/* ══ RESTAURANT INFO CARD ══ */}
      <div style={{ background:C.white, marginTop:-20, borderRadius:"20px 20px 0 0", padding:"20px 16px 0", position:"relative", zIndex:10 }}>

        <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:6 }}>
          <div style={{ flex:1 }}>
            <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1.5rem", letterSpacing:"-0.5px", lineHeight:1.2, color:C.text }}>
              {r.name || r.businessName || "Restaurant"}
            </div>
            <div style={{ fontSize:"0.82rem", color:C.sub, marginTop:4, fontWeight:500 }}>
              {Array.isArray(r.cuisine) ? r.cuisine.join(" · ") : r.cuisine || r.category || "Multi Cuisine"}
            </div>
          </div>
          {/* Restaurant logo / icon box */}
          <div style={{ width:52, height:52, borderRadius:14, background:"linear-gradient(135deg,#ea580c,#f97316)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, marginLeft:10, boxShadow:"0 4px 14px rgba(249,115,22,0.3)", overflow:"hidden" }}>
            {r.logo ? (
              <img src={r.logo} alt={r.name} style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
            ) : (
              <UtensilsCrossed size={24} color="#fff" strokeWidth={1.8}/>
            )}
          </div>
        </div>

        {/* Location */}
        <div style={{ display:"flex", alignItems:"center", gap:5, marginBottom:14 }}>
          <MapPin size={13} color={C.primary}/>
          <span style={{ fontSize:"0.78rem", color:C.sub }}>{r.area || r.city || "Akola"}</span>
          {r.address && <span style={{ fontSize:"0.72rem", color:C.muted }}> · {r.address}</span>}
        </div>

        {/* Stats row */}
        <div style={{ display:"flex", gap:0, background:C.bg, borderRadius:14, overflow:"hidden", marginBottom:16 }}>
          {[
            { Icon:Star,  val: r.rating || "4.1",         sub:"Rating",   color:C.green,   fill:true  },
            { Icon:Clock, val:`${r.deliveryTime||30} min`, sub:"Delivery", color:C.primary, fill:false },
          ].map((s, i) => (
            <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", padding:"12px 6px", borderRight: i<1 ? `1px solid ${C.border}` : "none" }}>
              <div style={{ display:"flex", alignItems:"center", gap:4, marginBottom:3 }}>
                <s.Icon size={14} color={s.color} fill={s.fill ? C.yellow : "none"}/>
                <span style={{ fontWeight:800, fontSize:"0.88rem", color:C.text }}>{s.val}</span>
              </div>
              <span style={{ fontSize:"0.65rem", color:C.muted, fontWeight:600 }}>{s.sub}</span>
            </div>
          ))}
        </div>

        {/* Open/Closed badge */}
        {r.isOpen === false && (
          <div style={{ background:C.redBg, border:"1px solid #fecaca", borderRadius:10, padding:"8px 14px", marginBottom:14, display:"flex", alignItems:"center", gap:8 }}>
            <div style={{ width:8, height:8, borderRadius:"50%", background:C.red }}/>
            <span style={{ fontSize:"0.8rem", fontWeight:700, color:C.red }}>Currently closed · Opens later today</span>
          </div>
        )}

        {/* Offer strip */}
        <div style={{ display:"flex", gap:8, overflowX:"auto", paddingBottom:14, scrollbarWidth:"none" }}>
          {[
            { Icon:BadgePercent, text:"20% off upto ₹80",        color:C.primary,    bg:C.light,    border:C.mid       },
            { Icon:Zap,          text:"Free delivery above ₹299", color:"#2563eb",   bg:"#eff6ff",  border:"#bfdbfe"   },
            { Icon:Tag,          text:"₹50 off on first order",   color:C.green,     bg:C.greenBg,  border:C.greenBorder},
          ].map((o, i) => (
            <div key={i} style={{ flexShrink:0, display:"flex", alignItems:"center", gap:6, background:o.bg, border:`1px solid ${o.border}`, borderRadius:100, padding:"5px 12px" }}>
              <o.Icon size={11} color={o.color}/>
              <span style={{ fontSize:"0.7rem", fontWeight:700, color:o.color, whiteSpace:"nowrap" }}>{o.text}</span>
            </div>
          ))}
        </div>

        <div style={{ height:1, background:C.border }}/>
      </div>

      {/* ══ STICKY CATEGORY TABS + FILTER ══ */}
      <div style={{ position:"sticky", top:0, zIndex:100, background:C.white, boxShadow: scrolled ? "0 2px 12px rgba(0,0,0,0.08)" : "none", transition:"box-shadow .2s" }}>
        <div ref={catBarRef} style={{ display:"flex", overflowX:"auto", scrollbarWidth:"none", borderBottom:`1px solid ${C.border}` }}>
          {categories.map(cat => (
            <button key={cat} data-cat={cat} className="cat-tab" onClick={() => scrollToCat(cat)}
              style={{ flexShrink:0, padding:"12px 16px", background:"none", cursor:"pointer", fontSize:"0.82rem", fontWeight: activeCat===cat ? 800 : 600, color: activeCat===cat ? C.primary : C.sub, borderBottom: activeCat===cat ? `2.5px solid ${C.primary}` : "2.5px solid transparent", transition:"all .2s", whiteSpace:"nowrap" }}>
              {cat}
            </button>
          ))}
        </div>

        <div style={{ display:"flex", gap:8, padding:"10px 16px", overflowX:"auto", scrollbarWidth:"none" }}>
          <button onClick={() => setSrchOpen(true)}
            style={{ flexShrink:0, display:"flex", alignItems:"center", gap:6, background:C.bg, border:`1px solid ${C.border}`, borderRadius:100, padding:"6px 12px", cursor:"pointer" }}>
            <Search size={13} color={C.sub}/><span style={{ fontSize:"0.74rem", fontWeight:600, color:C.sub }}>Search menu</span>
          </button>
          <button onClick={() => { setVegOnly(!vegOnly); setNonVegOnly(false); }}
            style={{ flexShrink:0, display:"flex", alignItems:"center", gap:6, background: vegOnly ? C.greenBg : C.bg, border:`1.5px solid ${vegOnly ? C.green : C.border}`, borderRadius:100, padding:"6px 12px", cursor:"pointer" }}>
            <VegDot veg={true} size={13}/>
            <span style={{ fontSize:"0.74rem", fontWeight:700, color: vegOnly ? C.green : C.sub }}>Veg Only</span>
          </button>
          <button onClick={() => { setNonVegOnly(!nonVegOnly); setVegOnly(false); }}
            style={{ flexShrink:0, display:"flex", alignItems:"center", gap:6, background: nonVegOnly ? C.redBg : C.bg, border:`1.5px solid ${nonVegOnly ? C.red : C.border}`, borderRadius:100, padding:"6px 12px", cursor:"pointer" }}>
            <VegDot veg={false} size={13}/>
            <span style={{ fontSize:"0.74rem", fontWeight:700, color: nonVegOnly ? C.red : C.sub }}>Non-Veg</span>
          </button>
        </div>
      </div>

      {/* ══ SEARCH OVERLAY ══ */}
      {srchOpen && (
        <div onClick={() => setSrchOpen(false)} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:400 }}>
          <div onClick={e => e.stopPropagation()} style={{ background:C.white, padding:"14px 16px" }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, background:C.bg, border:`2px solid ${C.primary}`, borderRadius:14, padding:"10px 14px" }}>
              <Search size={17} color={C.primary}/>
              <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search in menu..." style={{ flex:1, background:"transparent", fontSize:"0.92rem", color:C.text }}/>
              <button onClick={() => { setSearch(""); setSrchOpen(false); }}><X size={17} color={C.muted}/></button>
            </div>
          </div>
        </div>
      )}

      {/* ══ MENU ITEMS ══ */}
      <div style={{ padding:"8px 0 16px" }}>
        {filtered.length === 0 ? (
          <div style={{ textAlign:"center", padding:"60px 20px" }}>
            <UtensilsCrossed size={48} color={C.border} strokeWidth={1}/>
            <div style={{ fontSize:"0.95rem", fontWeight:700, color:C.sub, marginTop:12 }}>No items found</div>
            <div style={{ fontSize:"0.8rem", color:C.muted, marginTop:4 }}>Try removing filters</div>
          </div>
        ) : (
          <>
            <div style={{ padding:"14px 16px 10px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div>
                <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1.05rem", color:C.text }}>{activeCat}</div>
                <div style={{ fontSize:"0.72rem", color:C.muted, marginTop:2 }}>{filtered.length} items</div>
              </div>
            </div>

            <div style={{ display:"flex", flexDirection:"column" }}>
              {filtered.map((item, i) => {
                const qty = getQty(item._id);
                return (
                  <div key={item._id} className="item-card"
                    style={{ background:C.white, padding:"16px", display:"flex", gap:14, borderBottom:`1px solid ${C.border}`, transition:"box-shadow .2s", animation:`fadeIn .3s ${i*.04}s both` }}>

                    {/* Left: info */}
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ marginBottom:6 }}>
                        <VegDot veg={item.isVeg !== false}/>
                      </div>
                      <div style={{ fontWeight:800, fontSize:"0.95rem", color:C.text, marginBottom:4, lineHeight:1.3 }}>
                        {item.name}
                      </div>
                      <div style={{ fontWeight:800, fontSize:"1rem", color:C.text, marginBottom:6 }}>
                        ₹{item.price}
                      </div>
                      {item.description && (
                        <div style={{ fontSize:"0.76rem", color:C.muted, lineHeight:1.5, display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical", overflow:"hidden" }}>
                          {item.description}
                        </div>
                      )}
                      {item.isBestseller && (
                        <div style={{ display:"inline-flex", alignItems:"center", gap:4, background:"#fffbeb", border:"1px solid #fde68a", borderRadius:6, padding:"3px 8px", marginTop:8 }}>
                          <CheckCircle size={11} color={C.yellow} fill={C.yellow}/>
                          <span style={{ fontSize:"0.65rem", fontWeight:700, color:"#92400e" }}>Bestseller</span>
                        </div>
                      )}
                    </div>

                    {/* Right: image + add btn */}
                    <div style={{ flexShrink:0, display:"flex", flexDirection:"column", alignItems:"center", gap:10 }}>
                      <div style={{ width:110, height:110, borderRadius:14, overflow:"hidden", background:"linear-gradient(135deg,#ea580c20,#f9731620)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                        {item.image ? (
                          <img src={item.image} alt={item.name} style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
                        ) : (
                          <UtensilsCrossed size={36} color={C.mid} strokeWidth={1}/>
                        )}
                      </div>
                      <div style={{ marginTop:-22, position:"relative", zIndex:2 }}>
                        <QtyBtn qty={qty}
                          onAdd={() => qty === 0
                            ? addToCart({ ...item, restaurantId:r?._id, restaurantName:r?.name })
                            : increaseQty(item._id)}
                          onDec={() => decreaseQty(item._id)}
                        />
                      </div>
                      {item.customisable && (
                        <div style={{ fontSize:"0.62rem", color:C.muted, marginTop:2 }}>Customisable</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Restaurant info footer */}
        <div style={{ margin:"20px 16px 0", background:C.white, borderRadius:18, padding:"16px", border:`1px solid ${C.border}` }}>
          <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"0.95rem", color:C.text, marginBottom:12 }}>
            Restaurant Details
          </div>
          {[
            { Icon:MapPin, label:"Address",  val: r.address || r.area || "Akola, Maharashtra" },
            { Icon:Clock,  label:"Timings",  val: r.timings || "10:00 AM – 11:00 PM" },
            ...(r.phone ? [{ Icon:Phone, label:"Phone", val: r.phone }] : []),
          ].map((d, i, arr) => (
            <div key={i} style={{ display:"flex", gap:10, alignItems:"flex-start", marginBottom: i<arr.length-1 ? 10 : 0 }}>
              <div style={{ width:32, height:32, borderRadius:9, background:C.bg, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                <d.Icon size={15} color={C.primary}/>
              </div>
              <div>
                <div style={{ fontSize:"0.68rem", color:C.muted, fontWeight:600, marginBottom:1 }}>{d.label}</div>
                <div style={{ fontSize:"0.8rem", color:C.text, fontWeight:600 }}>{d.val}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ══ FLOATING CART BAR ══ */}
      {totalItems > 0 && (
        <div style={{ position:"fixed", bottom:16, left:16, right:16, zIndex:300, animation:"slideUp .3s ease" }}>
          <button onClick={() => navigate("/cart")}
            style={{ width:"100%", background:"linear-gradient(135deg,#c2410c,#f97316)", borderRadius:16, padding:"14px 18px", display:"flex", alignItems:"center", justifyContent:"space-between", cursor:"pointer", boxShadow:"0 8px 32px rgba(234,88,12,0.4)", border:"none" }}>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <div style={{ background:"rgba(255,255,255,0.2)", borderRadius:8, padding:"4px 10px", display:"flex", alignItems:"center", gap:5 }}>
                <ShoppingCart size={15} color="#fff"/>
                <span style={{ color:"#fff", fontWeight:800, fontSize:"0.82rem" }}>{totalItems} item{totalItems > 1 ? "s" : ""}</span>
              </div>
              <span style={{ color:"rgba(255,255,255,0.85)", fontSize:"0.78rem", fontWeight:600 }}>
                {r.name?.split(" ")[0] || "Restaurant"}
              </span>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <span style={{ color:"#fff", fontWeight:800, fontSize:"0.95rem" }}>₹{totalAmt}</span>
              <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                <span style={{ color:"#fff", fontWeight:700, fontSize:"0.82rem" }}>View Cart</span>
                <ChevronRight size={16} color="#fff"/>
              </div>
            </div>
          </button>
        </div>
      )}
    </div>
  );
}